import {sendDesktopAnalyticsEvent} from '@optimaxdev/analytics/desktop/lib/sendAnalyticsEvent/index';
import {
    PaymentIntentResult,
    PaymentRequest,
    PaymentRequestPaymentMethodEvent,
    Stripe,
} from '@stripe/stripe-js';

import {CHECKOUT_PAYMENT_STEP} from 'constants/analytics/events/checkout';
import {callbackWithAnalyticsEvent} from 'libs/analytics/callbackWithAnalytic';
import {PaymentDetailsType} from 'types/applePayType';

import {getApplePayClientSecret} from './paymentApplePayRequests';

type CreateStripeRequestType = {
    stripe: Stripe;
    paymentDetails: PaymentDetailsType;
    formKey: string;
    setApplePayError: (error: string) => void;
    setPaymentRequest: (request: PaymentRequest) => void;
    handlePayment: (
        evt: PaymentRequestPaymentMethodEvent,
        clientSecret: string,
        {error, paymentIntent}: PaymentIntentResult,
    ) => void;
};

/**
 * Create stripe request and create stripe events for ApplePayExpress component
 */
export const createStripeRequest = ({
    formKey,
    handlePayment,
    paymentDetails,
    setApplePayError,
    setPaymentRequest,
    stripe,
}: CreateStripeRequestType) => {
    const request = stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        requestPayerName: false,
        ...paymentDetails,
    });

    request.canMakePayment().then(result => {
        if (result && result.applePay) {
            setPaymentRequest(request);
            sendDesktopAnalyticsEvent(
                CHECKOUT_PAYMENT_STEP,
                {
                    message: 'Payment Method Available',
                    method: 'Apple Pay',
                },
                false,
            );
        }
    });

    request.on('paymentmethod', async evt => {
        const clientSecret = await getApplePayClientSecret(evt.paymentMethod.id, formKey);
        if (clientSecret) {
            const confirmData = await stripe.confirmCardPayment(
                clientSecret,
                {payment_method: evt.paymentMethod.id},
                {handleActions: false},
            );
            await callbackWithAnalyticsEvent('place order', 'Apple Pay', async () => {
                handlePayment(evt, clientSecret, confirmData);
            });
        } else {
            evt.complete('fail');
            setApplePayError('Something went wrong, please try again later');
            sendDesktopAnalyticsEvent(
                CHECKOUT_PAYMENT_STEP,
                {
                    message: `Error – couldn't get client secret`,
                    method: 'Apple pay',
                },
                false,
            );
            return;
        }
    });

    request.on('cancel', async () => {
        sendDesktopAnalyticsEvent(CHECKOUT_PAYMENT_STEP, {
            message: `Widget failed or closed by client`,
            method: 'Apple pay',
        });
    });
};
